// =============================================================================
// src/data/manaus-electoral.ts — Resultado eleitoral por zona/ano + paletas.
//
// Proveniencia: assets/unified-app.js DATA.electoral / DATA.layerColors /
//   DATA.legendData (linhas 12-77). Portado verbatim para preservar paridade de
//   dados com o mockup; valores marcados `as const` para inferir as tuplas
//   readonly do contrato (ZoneElectoralResult / LegendItem).
// =============================================================================

import type {
  ElectoralByYear,
  LayerColorsMap,
  LegendDataMap,
} from '../app/types';

/**
 * Serie historica eleitoral 2014-2024 das 6 zonas de Manaus (DATA.electoral).
 * Cada zona: [partidoVencedor, corHex, rotulo1o, rotulo2o].
 */
export const MANAUS_ELECTORAL_BY_YEAR: ElectoralByYear = {
  '2014': {
    norte: ['MDB', '#639922', 'MDB (44%)', 'PT (33%)'],
    leste: ['PT', '#E24B4A', 'PT (41%)', 'MDB (35%)'],
    'centro-oeste': ['PT', '#E24B4A', 'PT (38%)', 'PSDB (32%)'],
    'centro-sul': ['PSDB', '#7F77DD', 'PSDB (47%)', 'PT (29%)'],
    oeste: ['PT', '#E24B4A', 'PT (51%)', 'MDB (28%)'],
    sul: ['PSDB', '#7F77DD', 'PSDB (45%)', 'PT (30%)'],
  },
  '2016': {
    norte: ['MDB', '#639922', 'MDB (40%)', 'PT (28%)'],
    leste: ['PT', '#E24B4A', 'PT (38%)', 'PSD (30%)'],
    'centro-oeste': ['MDB', '#639922', 'MDB (36%)', 'PT (30%)'],
    'centro-sul': ['PSDB', '#7F77DD', 'PSDB (43%)', 'PSD (29%)'],
    oeste: ['PT', '#E24B4A', 'PT (47%)', 'MDB (26%)'],
    sul: ['PSDB', '#7F77DD', 'PSDB (42%)', 'PSD (28%)'],
  },
  '2018': {
    norte: ['MDB', '#639922', 'MDB (35%)', 'PSD (32%)'],
    leste: ['PSD', '#378ADD', 'PSD (39%)', 'PT (31%)'],
    'centro-oeste': ['PSD', '#378ADD', 'PSD (37%)', 'MDB (28%)'],
    'centro-sul': ['PL', '#185FA5', 'PL (44%)', 'PSDB (26%)'],
    oeste: ['PT', '#E24B4A', 'PT (42%)', 'PSD (27%)'],
    sul: ['PSD', '#378ADD', 'PSD (40%)', 'PL (29%)'],
  },
  '2020': {
    norte: ['Avante', '#1D9E75', 'Avante (48%)', 'MDB (24%)'],
    leste: ['Avante', '#1D9E75', 'Avante (43%)', 'PSD (28%)'],
    'centro-oeste': ['MDB', '#639922', 'MDB (33%)', 'Avante (31%)'],
    'centro-sul': ['PL', '#185FA5', 'PL (46%)', 'Avante (24%)'],
    oeste: ['PSD', '#378ADD', 'PSD (35%)', 'PT (32%)'],
    sul: ['PSD', '#378ADD', 'PSD (38%)', 'PL (30%)'],
  },
  '2022': {
    norte: ['Avante', '#1D9E75', 'Avante (51%)', 'PL (29%)'],
    leste: ['Avante', '#1D9E75', 'Avante (47%)', 'PL (26%)'],
    'centro-oeste': ['MDB', '#639922', 'MDB (34%)', 'Avante (29%)'],
    'centro-sul': ['PL', '#185FA5', 'PL (49%)', 'Avante (22%)'],
    oeste: ['PT', '#E24B4A', 'PT (44%)', 'PSD (24%)'],
    sul: ['PSD', '#378ADD', 'PSD (39%)', 'PL (31%)'],
  },
  '2024': {
    norte: ['Avante', '#1D9E75', 'Avante (52%)', 'PL (38%)'],
    leste: ['Avante', '#1D9E75', 'Avante (49%)', 'PL (40%)'],
    'centro-oeste': ['MDB', '#639922', 'MDB (36%)', 'Avante (32%)'],
    'centro-sul': ['PL', '#185FA5', 'PL (54%)', 'Avante (37%)'],
    oeste: ['PT', '#E24B4A', 'PT (46%)', 'PL (28%)'],
    sul: ['PSD', '#378ADD', 'PSD (41%)', 'PL (34%)'],
  },
};

/** Paletas tematicas por zona para as camadas nao-partidarias (DATA.layerColors). */
export const MANAUS_LAYER_COLORS: LayerColorsMap = {
  'heatmap-pct': {
    norte: '#EF9F27',
    leste: '#BA7517',
    'centro-oeste': '#FAC775',
    'centro-sul': '#854F0B',
    oeste: '#FAC775',
    sul: '#EF9F27',
  },
  vulnerabilidade: {
    norte: '#E24B4A',
    leste: '#F09595',
    'centro-oeste': '#F7C1C1',
    'centro-sul': '#FCEBEB',
    oeste: '#A32D2D',
    sul: '#F09595',
  },
  pertencimento: {
    norte: '#3B6D11',
    leste: '#639922',
    'centro-oeste': '#97C459',
    'centro-sul': '#C0DD97',
    oeste: '#3B6D11',
    sul: '#97C459',
  },
};

/** Metadados de legenda por camada (DATA.legendData). Itens: [rotulo, corHex]. */
export const MANAUS_LEGEND_DATA: LegendDataMap = {
  partido: {
    title: 'Partido mais votado',
    items: [
      ['Avante', '#1D9E75'],
      ['PL', '#185FA5'],
      ['PSD', '#378ADD'],
      ['PT', '#E24B4A'],
      ['MDB', '#639922'],
    ],
  },
  'heatmap-pct': {
    title: '% voto candidato A',
    items: [
      ['<30%', '#FAEEDA'],
      ['30-45%', '#FAC775'],
      ['45-60%', '#EF9F27'],
      ['>60%', '#854F0B'],
    ],
  },
  vulnerabilidade: {
    title: 'Índice de vulnerabilidade',
    items: [
      ['Baixa', '#FCEBEB'],
      ['Média', '#F09595'],
      ['Alta', '#E24B4A'],
      ['Crítica', '#A32D2D'],
    ],
  },
  pertencimento: {
    title: 'Índice de pertencimento',
    items: [
      ['Baixo', '#C0DD97'],
      ['Médio', '#97C459'],
      ['Alto', '#639922'],
      ['Muito alto', '#3B6D11'],
    ],
  },
};
